import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSettingsContext } from "../components/settings";
import {
    Container,
    Typography,
    Card,
    TextField,
    Button,
    Box,
    Alert,
    Stack,
    LinearProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { bulkEmailToUserId as bulkEmailToUserId } from "../controllers/user";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";

// Styled component for the file input
const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

function BulkEmailToAmplitudeCohort() {
    const { themeStretch } = useSettingsContext();

    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setError(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!file) {
            setError("Please select a CSV file");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await bulkEmailToUserId({
                file,
            });

            setResult(response);
            setFile(null);
            // Reset the file input by clearing its value
            const fileInput = document.getElementById(
                "csv-file-input"
            ) as HTMLInputElement;
            if (fileInput) fileInput.value = "";
        } catch (err: any) {
            setError(err.message || "An error occurred during upload");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Bulk Email to UserId | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "lg"}>
                <Typography variant="h4" gutterBottom mb={3}>
                    Bulk Email to UserId
                </Typography>

                <Card sx={{ p: 3 }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={3}>
                            {error && (
                                <Alert
                                    severity="error"
                                    onClose={() => setError(null)}
                                >
                                    {error}
                                </Alert>
                            )}

                            {result && (
                                <Alert
                                    severity="success"
                                    onClose={() => setResult(null)}
                                >
                                    Successfully processed.
                                </Alert>
                            )}

                            <Box>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ mb: 1 }}
                                >
                                    Upload CSV File
                                    <VisuallyHiddenInput
                                        id="csv-file-input"
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                </Button>

                                {file && (
                                    <Typography
                                        variant="body2"
                                        sx={{ ml: 1, display: "inline" }}
                                    >
                                        Selected: {file.name}
                                    </Typography>
                                )}

                                <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                >
                                    The CSV file should have an "Email" column
                                    with user email addresses
                                </Typography>
                            </Box>

                            {loading && <LinearProgress />}

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading || !file}
                            >
                                {loading
                                    ? "Processing..."
                                    : "Download UserId CSV"}
                            </Button>
                        </Stack>
                    </Box>
                </Card>
            </Container>
        </>
    );
}

export default BulkEmailToAmplitudeCohort;
